import { useLingui } from '@lingui/react/macro';

import { Flags } from '@watershed/constants/flags';
import {
  PermissionObjectType,
  PermissionType,
} from '@watershed/constants/permissions';
import { routeForEditReport } from '@watershed/shared-universal/dashboardRoutes';
import { RESOURCE_TYPE_PERMISSION_CONFIGS } from '@watershed/shared-universal/permissions/permissionsConfig';
import {
  Action,
  OrgLevelPermissionType,
  ParentLevelResourceType,
} from '@watershed/shared-universal/permissions/permissionTypes';

import { GQPermissionObjectFragment } from '../../../../generated/graphql-operations';
import { useFeatureFlag } from '../../../../utils/FeatureFlag';
import { useUserContext } from '../../../../utils/UserContext';
import { useShowProductFootprints } from '../../../materials/shared/hooks/useShowProductFootprints';

/**
 * Configuration for collapsed child rows in the permissions table.
 * This allows displaying a single collapsed row representing all child resources
 * for a parent resource, with explicit filtering logic.
 */
export type ChildRowConfig = {
  /**
   * Predicate to determine if this resource should have a collapsed child row.
   * Return true to create a child row, false to skip.
   *
   * Example: For reports, only Report (not FlexibleReport) should show child rows.
   */
  shouldShowFor: (resource: GQPermissionObjectFragment) => boolean;

  /**
   * Title for the collapsed child row. Only called when shouldShowFor returns true.
   * Can be a static string or a function that returns a string based on the resource.
   */
  title: string | ((resource: GQPermissionObjectFragment) => string);

  /**
   * Optional tooltip for the collapsed child row.
   * Can be a static string or a function that returns a string based on the resource.
   */
  tooltip?: string | ((resource: GQPermissionObjectFragment) => string);
};

export type ResourcePermissionSectionItemDef = {
  resourceType: ParentLevelResourceType;
  childResourceType?: PermissionObjectType;
  actions: Array<Action>;

  // 'Permission' by default, but can be overridden (e.g. 'Dataset > Datasource')
  objectListTitle?: string;

  // The name of the root group in the tree table e.g 'All datasets & datasources'
  allGroupingName: string;

  /**
   * Configuration for collapsed child rows. If set, creates a single row representing
   * all child resources for resources where shouldShowFor returns true.
   */
  childRowConfig?: ChildRowConfig;

  routeForResource?: (resourceId: string) => string;
};

export type OrgLevelPermissionsSectionItemDef = {
  orgLevelPermissions: Array<{
    permissionType: OrgLevelPermissionType;
    // true by default i.e. we hide only if shouldShow = false
    shouldShow?: boolean;
  }>;
};

// Permission sections either have individual permission items (e.g. global permissions)
// or a single resource type (e.g. Datasets > Datasource)
export type PermissionSectionItemDef = {
  // we don't show some of the sections,
  // depending on feature flags and watershed plans
  // true by default i.e. we hide only if shouldShow = false
  shouldShow?: boolean;
} & (ResourcePermissionSectionItemDef | OrgLevelPermissionsSectionItemDef);

export type PermissionSectionDef = {
  id: string;
  title: string;
  subtitle?: string;
  sectionItems: Array<PermissionSectionItemDef>;
  shouldShow: boolean;
};

function getResourceConfig(
  resourceType: ParentLevelResourceType
): Pick<
  ResourcePermissionSectionItemDef,
  'resourceType' | 'actions' | 'childResourceType'
> {
  return {
    resourceType,
    childResourceType:
      RESOURCE_TYPE_PERMISSION_CONFIGS[resourceType].childResourceType,
    actions: RESOURCE_TYPE_PERMISSION_CONFIGS[resourceType].actions,
  };
}

export function usePermissionsSections() {
  const { t } = useLingui();

  const hasOrgStructure = useFeatureFlag(Flags.OrgStructure);
  const { canAccessFinance, canAccessCorporate } = useUserContext();
  const hasCalculationCustomization = useFeatureFlag(
    Flags.CalculationCustomization
  );
  const hasImprovedFootprintExportWithActivityData = useFeatureFlag(
    Flags.ImprovedFootprintExportWithActivityData
  );
  const { showPfScope3, showPfProductFootprints } = useShowProductFootprints();
  const hasProductFootprints = showPfScope3 || showPfProductFootprints;

  const permissionsSections: Array<PermissionSectionDef> = [
    {
      id: 'global-permissions',
      title: t`Global permissions`,
      subtitle: t`Permissions that apply to all resources and features.`,
      sectionItems: [
        {
          orgLevelPermissions: [
            {
              permissionType: PermissionType.Admin,
            },
            {
              permissionType: PermissionType.CorporateAdmin,
              shouldShow: canAccessFinance && canAccessCorporate,
            },
            {
              permissionType: PermissionType.FinanceAdmin,
              shouldShow: canAccessFinance,
            },
            {
              permissionType: PermissionType.ManageMeasurement,
              shouldShow: canAccessCorporate,
            },
            {
              permissionType: PermissionType.ManageSingleSignOn,
            },
          ],
        },
      ],
      shouldShow: true,
    },
    {
      id: 'organizational-data',
      title: t`Organizational data`,
      subtitle: t`Manage access to your company's organizational structure.`,
      sectionItems: [
        {
          orgLevelPermissions: [
            {
              permissionType: PermissionType.ManageOrgHierarchy,
              shouldShow: hasOrgStructure,
            },
          ],
        },
      ],
      shouldShow: canAccessCorporate && hasOrgStructure,
    },
    {
      id: 'datasets',
      title: t`Datasets and datasources`,
      subtitle: t`Manage access to datasets and datasources.`,
      sectionItems: [
        {
          ...getResourceConfig(PermissionObjectType.Dataset),
          actions:
            RESOURCE_TYPE_PERMISSION_CONFIGS[PermissionObjectType.Dataset]
              .actions,
          objectListTitle: t`Dataset / Datasource`,
          allGroupingName: t`All datasets & datasources`,
        },
      ],
      shouldShow: canAccessCorporate,
    },
    {
      id: 'all-footprints',
      title: t({
        message: 'All footprints',
        context: 'Section header for organization-wide footprint permissions',
      }),
      subtitle: t`Manage access for all footprints.`,
      sectionItems: [
        {
          orgLevelPermissions: [
            {
              permissionType: PermissionType.ViewFootprintDetail,
            },
            {
              permissionType: PermissionType.ViewAuditDetail,
            },
          ],
        },
      ],
      shouldShow: canAccessCorporate,
    },
    {
      id: 'individual-footprints',
      title: t({
        message: 'Individual footprints',
        context: 'Section header for granular footprint permissions',
      }),
      subtitle: t`Manage access for specific footprints.`,
      sectionItems: [
        {
          resourceType: PermissionObjectType.Footprint,
          objectListTitle: t({
            message: 'Footprint',
            context:
              'Column header showing which footprint the user is granting permissions on',
          }),
          allGroupingName: t({
            message: 'All footprints',
            context:
              'Name of a row of controls that grant permissions to all footprints in an organization',
          }),
          shouldShow: true,
          actions: [Action.View, Action.Approve],
        },
      ],
      shouldShow: canAccessCorporate,
    },
    {
      id: 'footprint-exports',
      title: t({
        message: 'Footprint exports',
        context: 'Section header for granular footprint export permissions',
      }),
      subtitle: t`Manage access for specific footprint exports.`,
      sectionItems: [
        {
          resourceType: PermissionObjectType.FootprintExport,
          objectListTitle: t({
            message: 'Footprint export',
            context:
              'Column header showing which footprint export the user is granting permissions on',
          }),
          allGroupingName: t({
            message: 'All footprint exports',
            context:
              'Name of a row of controls that grant permissions to all footprint exports in an organization',
          }),
          shouldShow: hasImprovedFootprintExportWithActivityData,
          actions: [Action.View],
        },
      ],
      shouldShow:
        canAccessCorporate && hasImprovedFootprintExportWithActivityData,
    },
    {
      id: 'calculation',
      title: t({
        message: 'Calculation',
        context: 'Section header for calculation permissions',
      }),
      subtitle: t`Manage access for citations, reference tables, calculation methods, and customizations.`,
      sectionItems: [
        {
          orgLevelPermissions: [
            { permissionType: PermissionType.ViewCalculation },
            { permissionType: PermissionType.EditCalculation },
          ],
        },
      ],
      shouldShow: canAccessCorporate && hasCalculationCustomization,
    },
    {
      id: 'reports',
      title: t`Reports`,
      sectionItems: [
        {
          ...getResourceConfig(PermissionObjectType.Report),
          objectListTitle: t`Report / Report question`,
          allGroupingName: t`All reports & questions`,
          childRowConfig: {
            shouldShowFor: ({ __typename }) => __typename === 'Report',
            title: t`Report questions`,
            tooltip: t`View and edit report question permissions in the reporting module.`,
          },
          routeForResource: (resourceId) => routeForEditReport(resourceId),
        },
      ],
      shouldShow: canAccessCorporate,
    },
    {
      id: 'supply-chain',
      title: t`Supply chain`,
      subtitle: t`Manage access to supply chain data.`,
      sectionItems: [
        {
          orgLevelPermissions: [
            {
              permissionType: PermissionType.ManageDisclosures,
            },
            {
              permissionType: PermissionType.ManageSuppliers,
              shouldShow: canAccessCorporate,
            },
          ],
        },
      ],
      shouldShow: true,
    },
    {
      id: 'product-footprints',
      title: t`Product footprints`,
      subtitle: t`Manage access to product footprints.`,
      sectionItems: [
        {
          orgLevelPermissions: [
            {
              permissionType: PermissionType.EditProductFootprint,
            },
            {
              permissionType: PermissionType.ViewProductFootprint,
            },
          ],
        },
      ],
      shouldShow: canAccessCorporate && hasProductFootprints,
    },
    {
      id: 'reductions',
      title: t`Reductions`,
      subtitle: t`Manage access to reduction targets and initiatives.`,
      sectionItems: [
        {
          orgLevelPermissions: [
            {
              permissionType: PermissionType.ManageReductionPlans,
            },
            {
              permissionType: PermissionType.ViewReductions,
            },
          ],
        },
      ],
      shouldShow: canAccessCorporate,
    },
    {
      id: 'marketplace',
      title: t`Marketplace`,
      sectionItems: [
        {
          orgLevelPermissions: [
            {
              permissionType: PermissionType.ManageMarketplacePurchases,
            },
          ],
        },
      ],
      shouldShow: canAccessCorporate,
    },
    {
      id: 'finance',
      title: t`Finance`,
      subtitle: t`Manage access to the finance product and features.`,
      sectionItems: [
        {
          ...getResourceConfig(PermissionObjectType.Fund),
          allGroupingName: t`All funds`,
        },
      ],
      shouldShow: canAccessFinance,
    },
  ];

  return filterOutHiddenPermissions(permissionsSections);
}

function filterOutHiddenPermissions(
  permissionsSections: Array<PermissionSectionDef>
): Array<PermissionSectionDef> {
  return permissionsSections
    .filter((_) => _.shouldShow)
    .map((section) => ({
      ...section,
      sectionItems: section.sectionItems
        .filter((item) => item.shouldShow !== false)
        .map((item) =>
          'orgLevelPermissions' in item
            ? {
                ...item,
                orgLevelPermissions: item.orgLevelPermissions.filter(
                  (orgLevelPermission) =>
                    orgLevelPermission.shouldShow !== false
                ),
              }
            : item
        ),
    }));
}

export const exportedForTesting = {
  filterOutHiddenPermissions,
  getResourceConfig,
};
